import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import api from "../../api/axios";
import { toast } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import { FileButton, FileItem } from "./styled";
import dayjs from "dayjs";

export const OccurrenceFilesModal = ({ open, onClose, occurrence }) => {
  const fileInputRef = useRef();

  const [files, setFiles] = useState([]);

  const [isAdding, setIsAdding] = useState(false);
  const [file, setFile] = useState(null);

  const [isSendingForm, setIsSendingForm] = useState(false);

  const [newFile, setNewFile] = useState({
    occurrence: "",
    name: "",
    addedIn: null,
  });

  const getFiles = async (item) => {
    try {
      const { data } = await api.get(
        "/occurrence_file/get-by-occurrence/" + item._id,
      );

      setFiles(data);
    } catch (err) {
      console.error(err);
      toast.error("Ocorreu um erro");
    }
  };

  const handleSubmit = async () => {
    setIsSendingForm(true);

    const isFormValid = Object.values(newFile).every((v) => v !== "");

    if (!isFormValid) {
      toast.error("Por favor, preencha todos os campos!");
      setIsSendingForm(false);
      return;
    }

    if (!newFile.addedIn) newFile.addedIn = new Date();

    try {
      const formData = new FormData();

      formData.append("values", JSON.stringify({ ...newFile }));
      formData.append("file", file);

      await api.post("/occurrence_file/create", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      getFiles(occurrence);
      resetValues();
      setIsAdding(false);
    } catch (err) {
      console.error(err);
      toast.error("Ocorreu um erro");
    } finally {
      setIsSendingForm(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setIsSendingForm(true);

      await api.delete("/occurrence_file/delete/" + id);
      getFiles(occurrence);
    } catch (err) {
      console.error(err);
      toast.error("Ocorreu um erro");
    } finally {
      setIsSendingForm(false);
    }
  };

  const handleClose = () => {
    setIsAdding(false);
    resetValues();
    onClose();
  };

  const handleAddClick = () => {
    setIsAdding(true);
  };

  const handleCancelClick = () => {
    resetValues();
    setIsAdding(false);
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
  };

  const handleFileClick = (e) => {
    e.preventDefault();
    fileInputRef.current.click();
  };

  const resetValues = () => {
    setNewFile({
      occurrence: occurrence._id,
      name: "",
      addedIn: null,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setFile(null);
  };

  useEffect(() => {
    if (occurrence) {
      getFiles(occurrence);
      setNewFile({ ...newFile, occurrence: occurrence._id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occurrence]);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "900px",
          minWidth: "300px",
        },
      }}
    >
      <DialogTitle textAlign="center">{"Documentos da ocorrência"}</DialogTitle>
      <DialogContent>
        <Stack
          sx={{
            width: "100%",
            minWidth: "300px",
            gap: "1.5rem",
          }}
        >
          {files.length > 0 ? (
            files.map((file) => (
              <div key={file._id} style={{ display: "flex", gap: "5px" }}>
                <FileItem onClick={() => window.open(file.url, "_blank")}>
                  <span>{file.name}</span>
                  <span
                    style={{ color: "#777", fontSize: "0.75rem" }}
                  >{`Adicionado em: ${dayjs(file.addedIn).format("DD/MM/YYYY")}`}</span>
                </FileItem>
                <div style={{ margin: "auto 0" }}>
                  <Tooltip arrow placement="right" title="Remover">
                    <IconButton
                      disabled={isSendingForm}
                      onClick={() => handleDelete(file._id)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))
          ) : (
            <div style={{ margin: "0 auto" }}>
              <span>
                {"Não existem arquivos relacionados a essa ocorrência"}
              </span>
            </div>
          )}
          {!isAdding && (
            <Button
              sx={{ color: "#104467" }}
              onClick={handleAddClick}
              disabled={isSendingForm}
            >
              {"+ Adicionar Arquivo"}
            </Button>
          )}
          {isAdding && (
            <>
              <hr />
              <TextField
                label="Nome do arquivo"
                id="name"
                name="name"
                type="text"
                value={newFile.name}
                onChange={(e) => {
                  setNewFile({ ...newFile, name: e.target.value });
                }}
              />
              <FileButton onClick={handleFileClick} disabled={isSendingForm}>
                {"Adicionar Arquivo"}
              </FileButton>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <span
                style={{
                  color: "#104467",
                  fontSize: "0.8rem",
                  margin: "0 auto",
                }}
              >
                {file ? `Arquivo adicionado: ${file.name}` : ""}
              </span>
              <div
                style={{ display: "flex", justifyContent: "end", gap: "15px" }}
              >
                <Button
                  sx={{ color: "#104467" }}
                  onClick={handleCancelClick}
                  disabled={isSendingForm}
                >
                  Cancelar
                </Button>
                <Button
                  sx={{ backgroundColor: "#104467", color: "white" }}
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={isSendingForm}
                >
                  {"Salvar"}
                </Button>
              </div>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: "1.25rem" }}>
        <Button sx={{ color: "#104467" }} onClick={handleClose}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
